import type { FC, ReactNode } from 'react'
import type { TerrainScene } from '@/lib/terrain/terrain-scene'

import { useRef } from 'react'
import { Plus } from 'lucide-react'
import { terrainMarkers } from '@/constants/content'

import { lerp, smoothstep } from '@/lib/motion/math'

import {
    emptyMetrics,
    isNear,
    measureSection,
    pinRaw,
    useEngine
} from '@/lib/motion/section'

import { Bracket, createStyleCache } from '@/components/landing/primitives'
import { getAppState, subscribeAppState } from '@/lib/app-store'
import { whenIdle } from '@/lib/motion/idle'
import { live } from '@/lib/drone/control'

const SKY = 'linear-gradient(180deg, #cfe3eb 0%, #dcebf0 42%, #e2eef2 70%, #e8f1f4 100%)'

const TerrainSection: FC = (): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const frameRef = useRef<HTMLDivElement>(null)
    const bloomRef = useRef<HTMLDivElement>(null)
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const introRef = useRef<HTMLDivElement>(null)
    const hudRef = useRef<HTMLDivElement>(null)
    const coordsRef = useRef<HTMLSpanElement>(null)
    const altitudeRef = useRef<HTMLSpanElement>(null)
    const markerRefs = useRef<Array<HTMLDivElement | null>>([])
    const lineRefs = useRef<Array<HTMLSpanElement | null>>([])

    useEngine(() => {
        const set = createStyleCache()

        let metrics = emptyMetrics()
        let scene: TerrainScene | null = null
        let heads: Array<TerrainScene['peaks'][number]['position']> = []
        let disposed = false
        let loading = false
        let frameWidth = 0
        let frameHeight = 0
        let lastCoords = ''
        let lastAltitude = ''

        const load = (): void => {
            if (loading || !canvasRef.current) return

            loading = true

            import('@/lib/terrain/terrain-scene')
                .then(({ TerrainScene: Scene }) => {
                    if (disposed || !canvasRef.current) return

                    try {
                        scene = new Scene(canvasRef.current)
                        scene.resize(frameWidth, frameHeight)
                        heads = scene.peaks.map(peak => peak.position.clone().setY(peak.position.y + 2.8))
                    }
                    catch (error) {
                        console.error('[orvane] terrain scene could not start', error)
                    }
                })
                .catch(error => console.error('[orvane] terrain scene failed to load', error))
        }

        let preloadTimer = 0

        const preload = (): void => {
            preloadTimer = window.setTimeout(() => whenIdle(load), 2200)
        }

        const offLoaded = subscribeAppState(() => {
            if (getAppState().loaded && !preloadTimer) preload()
        })

        if (getAppState().loaded) preload()

        return {
            measure: () => {
                if (sectionRef.current) metrics = measureSection(sectionRef.current, frameRef.current)

                frameWidth = frameRef.current?.clientWidth ?? window.innerWidth
                frameHeight = frameRef.current?.clientHeight ?? window.innerHeight
                scene?.resize(frameWidth, frameHeight)
            },
            frame: (state) => {
                if (!scene && isNear(metrics, state.scroll, state.vh, 3)) load()
                if (!isNear(metrics, state.scroll, state.vh, 0.05)) return

                const raw = pinRaw(metrics, state.scroll)
                const bloom = smoothstep(0.04, 0.3, raw)
                const eased = 1 - Math.pow(1 - bloom, 3)
                const top = lerp(state.mobile ? 60 : 58, 0, eased)
                const side = lerp(state.mobile ? 4 : 6, 0, eased)
                const radius = lerp(24, 0, eased)

                set(bloomRef.current, 'clip-path', `inset(${top.toFixed(3)}% ${side.toFixed(3)}% ${side.toFixed(3)}% ${side.toFixed(3)}% round ${radius.toFixed(2)}px)`)
                set(introRef.current, 'opacity', (1 - smoothstep(0.08, 0.22, raw)).toFixed(3))
                set(hudRef.current, 'opacity', smoothstep(0.22, 0.34, raw).toFixed(3))

                const current = scene

                if (!current) return

                const progress = smoothstep(0.26, 1, raw)

                current.render({
                    progress,
                    reveal: 0.35 + 0.65 * smoothstep(0.02, 0.3, raw),
                    time: state.time,
                    mouseX: state.mouseX,
                    mouseY: state.mouseY,
                    yaw: live.camera.yaw,
                    pitch: live.camera.pitch,
                    dolly: live.camera.dolly,
                    lift: live.camera.lift
                })

                const markersVisible = smoothstep(0.26, 0.36, raw)

                current.peaks.forEach((peak, index) => {
                    const marker = markerRefs.current[index]
                    const line = lineRefs.current[index]

                    if (!marker || !heads[index]) return

                    const ground = current.project(peak.position)
                    const head = current.project(heads[index])
                    const onScreen = ground.visible && head.visible && head.x > 16 && head.x < frameWidth - 16 && head.y > 70 && ground.y < frameHeight - 40

                    set(marker, 'transform', `translate3d(${head.x.toFixed(1)}px, ${head.y.toFixed(1)}px, 0)`)
                    set(marker, 'opacity', (onScreen ? markersVisible : 0).toFixed(3))
                    set(marker, 'visibility', onScreen && markersVisible > 0.02 ? 'visible' : 'hidden')
                    const sideName = head.x > frameWidth * 0.62 ? 'left' : 'right'

                    if (marker.dataset.side !== sideName) marker.dataset.side = sideName

                    if (line) set(line, 'height', `${Math.max(0, ground.y - head.y).toFixed(1)}px`)
                })

                const camera = current.camera.position
                const coords = `${(46.5583 - camera.z * 0.0011).toFixed(4)}° N  ${(8.0047 + camera.x * 0.0016).toFixed(4)}° E`
                const altitude = `ALT ${Math.round(2380 + camera.y * 52).toLocaleString('en-US')} M`

                if (coords !== lastCoords && coordsRef.current) {
                    lastCoords = coords
                    coordsRef.current.textContent = coords
                }

                if (altitude !== lastAltitude && altitudeRef.current) {
                    lastAltitude = altitude
                    altitudeRef.current.textContent = altitude
                }
            },
            cleanup: () => {
                disposed = true
                offLoaded()
                window.clearTimeout(preloadTimer)
                scene?.dispose()
                scene = null
            }
        }
    })

    return (
        <section id='terrain' ref={sectionRef} aria-labelledby='terrain-title' className='relative h-[380vh] bg-mist md:h-[400vh]'>
            <div ref={frameRef} data-frame='' className='sticky top-0 h-svh overflow-hidden bg-mist'>
                <div ref={introRef} className='absolute inset-x-5 top-[84px] flex items-start justify-between gap-6 md:inset-x-10'>
                    <div className='flex flex-col gap-3'>
                        <h2 id='terrain-title' className='label-mono text-ink'>
                            [ Terrain ]
                        </h2>
                        <p className='max-w-[300px] text-[15px] leading-[1.45] tracking-[-0.01em] text-haze'>
                            An entire range, flown in one pass and drawn line by line as it is scanned.
                        </p>
                    </div>

                    <span className='label-mono hidden text-haze md:inline'>Live pass</span>
                </div>

                <div aria-hidden='true' data-anchor='terrain-drone' className='absolute left-1/2 top-[34%] size-px' />

                <div
                    ref={bloomRef}
                    className='absolute inset-0 overflow-hidden'
                    style={{ clipPath: 'inset(58% 6% 6% 6% round 24px)' }}
                >
                    <div aria-hidden='true' className='absolute inset-0' style={{ background: SKY }} />

                    <canvas ref={canvasRef} aria-hidden='true' className='absolute inset-0 block size-full' />

                    <div aria-hidden='true' className='pointer-events-none absolute inset-0 bg-[radial-gradient(120%_70%_at_50%_0%,rgba(255,255,255,0.35),rgba(255,255,255,0)_60%)]' />

                    {terrainMarkers.map((marker, index) => (
                        <div
                            key={marker.id}
                            ref={(element) => { markerRefs.current[index] = element }}
                            data-side='right'
                            className='group absolute left-0 top-0 z-10'
                            style={{ opacity: 0, visibility: 'hidden' }}
                        >
                            <span
                                ref={(element) => { lineRefs.current[index] = element }}
                                aria-hidden='true'
                                className='absolute left-0 top-0 w-px bg-gradient-to-b from-ink/70 to-ink/10'
                                style={{ height: 0 }}
                            />

                            <span aria-hidden='true' className='absolute left-0 top-0 size-12 -translate-x-1/2 -translate-y-1/2 rounded-full border border-ink/15' />

                            <button
                                type='button'
                                aria-label={`${marker.name} readings`}
                                className='absolute left-0 top-0 flex size-8 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-ink text-white shadow-[0_8px_24px_-8px_rgba(29,42,49,0.6)] transition-transform duration-300 ease-out group-hover:scale-110 group-focus-within:scale-110'
                            >
                                <Plus aria-hidden='true' className='size-4 transition-transform duration-500 group-hover:rotate-90 group-focus-within:rotate-90' />
                            </button>

                            <div
                                className='pointer-events-none absolute left-6 top-0 w-56 -translate-y-1/2 scale-90 rounded-2xl border border-white/70 bg-white/85 p-4 opacity-0 shadow-[0_18px_40px_-18px_rgba(29,42,49,0.45)] backdrop-blur-md transition-all duration-300 ease-out group-hover:scale-100 group-hover:opacity-100 group-focus-within:scale-100 group-focus-within:opacity-100 group-data-[side=left]:left-auto group-data-[side=left]:right-6 origin-left group-data-[side=left]:origin-right'
                            >
                                <p className='label-mono text-ink'>{marker.name}</p>

                                <dl className='mt-3 flex flex-col gap-1.5'>
                                    {marker.readings.map(([label, value]) => (
                                        <div key={label} className='flex items-baseline justify-between gap-4 border-t border-line pt-1.5'>
                                            <dt className='text-[12px] text-haze'>{label}</dt>
                                            <dd className='text-[14px] tabular-nums tracking-[-0.02em] text-ink'>{value}</dd>
                                        </div>
                                    ))}
                                </dl>
                            </div>
                        </div>
                    ))}

                    <div ref={hudRef} className='pointer-events-none absolute inset-x-4 bottom-4 flex items-end justify-between gap-4 opacity-0 md:inset-x-8 md:bottom-7'>
                        <div className='flex flex-col gap-1.5'>
                            <Bracket className='text-ink'>Region</Bracket>
                            <span className='label-mono text-ink/70'>High ridge survey, sector N4</span>
                        </div>

                        <div className='flex flex-col items-end gap-1.5 text-right'>
                            <span ref={coordsRef} className='label-mono tabular-nums text-ink'>
                                46.5583° N  8.0047° E
                            </span>
                            <span ref={altitudeRef} className='label-mono tabular-nums text-ink/70'>
                                ALT 3,180 M
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default TerrainSection
